import { Divider, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TableGridWithFilters from '../../../components/TablegridWithFilters/TableGridWithFilters/TableGridWithFilters';
import Theme from '../../../Themes/main.theme';
import { GridColDef } from '@mui/x-data-grid';
import LaunchIcon from '@mui/icons-material/Launch';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import React from 'react';
import { GetSortOrder, createSearchParamsFormat, transformGreekToEnglish } from '../../../lib/helperFuncs';
import { FieldControllerTypes } from '../../../components/form_v2/types';
import { fetchErgoTypesOptions } from '../../../lib/fetchers';
import i18n from '../../../i18n';

const _initialValues = {
  ergoTitle: '',
  ergoType: '',
  keywords: '',
  toDate: '',
  fromDate: '',
  suntelestisName: '',
};

function Ergo() {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const matchesXSbreakpoint = useMediaQuery('(max-width:768px)');

  const [initialValues, setInitialValues] = React.useState(_initialValues);
  const [ergoTypeOptions, setErgoTypeOptions] = React.useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [fetchUrl, setFetchUrl] = React.useState<string>(`${process.env.REACT_APP_BACKEND_URL}/ergo?is_public=1&${searchParams.toString()}`);

  document.title = t('Αναζήτηση Οπτικοακουστικών Έργων');

  const mobileFilters = [
    {
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Τίτλος Έργου'),
      customLabel: true,
      name: 'ergoTitle',
      id: 'ergoTitle',
      filtersDesign: true,
    },
    {
      control: FieldControllerTypes.autocomplete,
      label: t('Τύπος Έργου'),
      name: 'ergoType',
      id: 'ergoType',
      filtersDesign: true,
      options: ergoTypeOptions,
    },
    {
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Λέξεις-Κλειδιά'),
      customLabel: true,
      name: 'keywords',
      id: 'keywords',
      filtersDesign: true,
    },
    {
      control: FieldControllerTypes.input,
      label: t('Έτος Από'),
      name: 'fromDate',
      id: 'fromDate',
      filtersDesign: true,
      type: 'number',
      positives_only: true,
      customLabel: true,
    },
    {
      control: FieldControllerTypes.input,
      label: t('Έτος Έως'),
      name: 'toDate',
      id: 'toDate',
      filtersDesign: true,
      type: 'number',
      positives_only: true,
      customLabel: true,
    },
    {
      control: FieldControllerTypes.input,
      label: t('Εμπλεκόμενοι'),
      type: 'search',
      customLabel: true,
      name: 'suntelestisName',
      id: 'suntelestisName',
      filtersDesign: true,
    },
  ];

  const fullScreen = [
    [{
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Τίτλος Έργου'),
      customLabel: true,
      name: 'ergoTitle',
      id: 'ergoTitle',
      filtersDesign: true,
    },
    {
      control: FieldControllerTypes.autocomplete,
      label: t('Τύπος Έργου'),
      name: 'ergoType',
      id: 'ergoType',
      filtersDesign: true,
      options: ergoTypeOptions
    },
    {
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Λέξεις-Κλειδιά'),
      customLabel: true,
      name: 'keywords',
      id: 'keywords',
      filtersDesign: true,
    }],
    [{
      control: FieldControllerTypes.input,
      label: t('Έτος Από'),
      name: 'fromDate',
      id: 'fromDate',
      filtersDesign: true,
      type: 'number',
      positives_only: true,
      customLabel: true
    },
    {
      control: FieldControllerTypes.input,
      label: t('Έτος Έως'),
      name: 'toDate',
      id: 'toDate',
      filtersDesign: true,
      type: 'number',
      positives_only: true,
      customLabel: true
    },
    {
      control: FieldControllerTypes.input,
      label: t('Εμπλεκόμενοι'),
      type: 'search',
      customLabel: true,
      name: 'suntelestisName',
      id: 'suntelestisName',
      filtersDesign: true,
    }
    ]
  ];


  const columns: GridColDef[] = [
    {
      field: 'projects:projectTitle',
      headerName: t('Τιτλος Έργου στα Ελληνικά') || undefined,
      minWidth: 350,
      renderCell: ({ row }) => row?.['projects:projectTitle']?.[0]?.['@value']
    },
    {
      field: 'projects:projectEnTitle',
      headerName: t('Τίτλος Έργου στα Αγγλικά') || undefined,
      minWidth: 350,
      renderCell: ({ row }) => row?.['projects:projectEnTitle']?.[0]?.['@value'] || '-'
    },
    {
      field: 'projects:projectAlternariveTitle',
      headerName: t('Εναλλακτικός Tίτλος') || undefined,
      minWidth: 300,
      renderCell: ({ row }) => i18n.language == 'en'
        ? transformGreekToEnglish(row?.['projects:projectAlternativeTitle']?.[0]?.['@value']) || '-'
        : row?.['projects:projectAlternativeTitle']?.[0]?.['@value'] || '-'
    },
    {
      field: 'dcterms:type',
      headerName: t('Τύπος Έργου') || undefined,
      minWidth: 240,
      renderCell: ({ row }) => t(row?.['projects:projectRelationWithTypeOfProject']?.[0]?.['@value']) || '-'
    },
    {
      field: 'projects:projectYear',
      headerName: t('Χρονολογία') || undefined,
      minWidth: 120,
      renderCell: ({ row }) => row?.['projects:projectYear']?.[0]?.['@value'] || '-'
    },
    {
      field: 'info',
      headerName: t('Λεπτομέρειες') || undefined,
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) =>
        <Tooltip PopperProps={{
          disablePortal: true
        }}
        title={t('Προβολή')}>
          <IconButton onClick={() => navigate(`/search-projects/details/${row['o:id']}`)}>
            <LaunchIcon color='primary' /></IconButton>
        </Tooltip>
    },

  ];

  const onSubmit = (values: any) => {
    const x = createSearchParamsFormat(values);
    Object.keys(initialValues).map((value) => {
      searchParams.delete(value);
      setSearchParams(searchParams);
    });
    setFetchUrl(`${process.env.REACT_APP_BACKEND_URL}/ergo?${searchParams.toString()}&${createSearchParams(x).toString()}`);
    setSearchParams(`${searchParams.toString()}&${createSearchParams(x).toString()}`);
  };


  const fetchErga = async () => {
    const ergoTypeOptions = await fetchErgoTypesOptions();
    const options = ergoTypeOptions?.['o:terms'].map((x: any) => {
      return {
        key: t(x),
        value: x
      };
    });
    setErgoTypeOptions(i18n.language === 'en'
      ? options.sort(GetSortOrder('key'))
      : options.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el'))
    );
  };

  React.useEffect(() => {
    let _params = { ..._initialValues };
    for (const entry of searchParams.entries()) {
      const [param, value] = entry;
      if (searchParams.has(param) && param in _initialValues) {
        if (param === 'ergoType') {
          const _x = value.split(',').map((valueOfParam: any) => {
            return {
              key: valueOfParam,
              value: valueOfParam
            };
          });
          _params = { ..._params, [param as string]: _x };
        } else _params = { ..._params, [param]: value };
      }
    }
    setInitialValues({ ..._params });
  }, []);

  React.useEffect(() => {
    let _params = { ..._initialValues };
    for (const entry of searchParams.entries()) {
      const [param, value] = entry;
      if (searchParams.has(param) && param in _initialValues) {
        if (param === 'ergoType') {
          const _x = value.split(',').map((valueOfParam: any) => {
            return {
              key: valueOfParam,
              value: valueOfParam
            };
          });
          _params = { ..._params, [param as string]: _x };
        } else {
          _params = { ..._params, [param]: value };
        }
      }
    }
    setInitialValues({ ..._params });
  }, [searchParams]);

  React.useEffect(() => {
    fetchErga();
  }, []);

  return (
    <>
      <Typography sx={{ my: 2 }} variant='h2'
        color={Theme?.labelColor?.main}>{t('Αναζήτηση Οπτικοακουστικών Έργων')}</Typography>
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
      <TableGridWithFilters
        responseDataFieldName='data'
        responseTotalCountFieldName='totalResults'
        sortingMode='server'
        paginationMode='server'
        pageSize={15}
        rowsPerPageOptions={[15, 30, 45]}
        initialValues={initialValues}
        filterFields={matchesXSbreakpoint
          ? mobileFilters
          : fullScreen}
        columns={columns}
        onFiltersChange={(values: any) => onSubmit(values)}
        hidePagination={false}
        resetFilters={true}
        ignoreFilters
        resetFiltersTo={_initialValues}
        fetchUrl={fetchUrl}
      />
    </>
  );
}

export default Ergo;